import React, { useState } from 'react';
import { Megaphone, Plus } from 'lucide-react';
import { AdminUser } from '../services/api.types';
import { useTranslations } from '../translations';
import './AdminUserDetailModal.css';

interface AdminUserDetailModalProps {
  user: AdminUser;
  complaints?: string[];
  onClose: () => void;
  onViewDetails?: (userId: string) => void;
  onBlockUnblock?: (userId: string, newStatus: 'ACTIVE' | 'BLOCKED', reason: string) => void;
  onIncreaseAnnouncementLimit?: (userId: string, announcementLimit: number) => Promise<void>;
}

const AdminUserDetailModal: React.FC<AdminUserDetailModalProps> = ({
  user,
  complaints,
  onClose,
  onViewDetails,
  onBlockUnblock,
  onIncreaseAnnouncementLimit,
}) => {
  const t = useTranslations();
  const isBlocked = user.status === 'BLOCKED';
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [newAnnouncementLimit, setNewAnnouncementLimit] = useState('');
  const [announcementLimitError, setAnnouncementLimitError] = useState<string | null>(null);
  const [isIncreasingLimit, setIsIncreasingLimit] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const currentAnnouncementLimit = user.announcementLimit ?? 50;
  const announcementCount = user.announcementCount ?? 0;

  const handleBlockUnblock = () => {
    if (onBlockUnblock) {
      const newStatus = isBlocked ? 'ACTIVE' : 'BLOCKED';
      onBlockUnblock(user.id, newStatus, blockReason.trim());
    }
  };

  const handleIncreaseAnnouncementLimit = async () => {
    const parsedLimit = Number(newAnnouncementLimit);
    if (
      !Number.isInteger(parsedLimit)
      || parsedLimit <= currentAnnouncementLimit
      || parsedLimit > 100000
    ) {
      setAnnouncementLimitError(t.administration.users.announcementLimitInvalid);
      return;
    }

    if (!onIncreaseAnnouncementLimit) return;

    setAnnouncementLimitError(null);
    setIsIncreasingLimit(true);
    try {
      await onIncreaseAnnouncementLimit(user.id, parsedLimit);
      setNewAnnouncementLimit('');
    } catch {
      // The parent displays the API error notification; keep the entered value for retry.
    } finally {
      setIsIncreasingLimit(false);
    }
  };

  return (
    <div className="admin-user-detail-modal-overlay" onClick={onClose}>
      <div className="admin-user-detail-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-user-detail-modal-header">
          <h3 className="admin-user-detail-modal-title">Detalii Utilizator</h3>
          <button className="admin-user-detail-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#071105" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="admin-user-detail-modal-body">
          <div className="admin-user-detail-avatar-section">
            {user.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={`${user.firstName} ${user.lastName}`}
                className="admin-user-detail-avatar"
                onClick={() => setShowImagePreview(true)}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <div className="admin-user-detail-avatar-placeholder">
                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
              </div>
            )}
            <div className="admin-user-detail-name-section">
              <h4 className="admin-user-detail-name">{user.firstName} {user.lastName}</h4>
              <p className="admin-user-detail-email">{user.email}</p>
            </div>
          </div>

          {user.description && (
            <div className="admin-user-detail-section">
              <label className="admin-user-detail-label">Descriere:</label>
              <p className="admin-user-detail-text">{user.description}</p>
            </div>
          )}

          <div className="admin-user-detail-section admin-user-announcement-section">
            <div className="admin-user-announcement-heading">
              <div className="admin-user-announcement-heading-icon">
                <Megaphone aria-hidden />
              </div>
              <div>
                <label className="admin-user-detail-label">
                  {t.administration.users.announcementUsage}
                </label>
                <p className="admin-user-announcement-summary">
                  <strong>{announcementCount}</strong> din {currentAnnouncementLimit}
                </p>
              </div>
            </div>

            {onIncreaseAnnouncementLimit && (
              <div className="admin-user-announcement-limit-control">
                <div className="admin-user-announcement-limit-field">
                  <label htmlFor="admin-user-announcement-limit">
                    {t.administration.users.newAnnouncementLimit}
                  </label>
                  <input
                    id="admin-user-announcement-limit"
                    type="number"
                    min={currentAnnouncementLimit + 1}
                    max={100000}
                    step={1}
                    inputMode="numeric"
                    value={newAnnouncementLimit}
                    onChange={(event) => {
                      setNewAnnouncementLimit(event.target.value);
                      setAnnouncementLimitError(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && newAnnouncementLimit) {
                        void handleIncreaseAnnouncementLimit();
                      }
                    }}
                    aria-invalid={Boolean(announcementLimitError)}
                    aria-describedby="admin-user-announcement-limit-help"
                  />
                </div>
                <button
                  type="button"
                  className="admin-user-announcement-limit-button"
                  onClick={() => void handleIncreaseAnnouncementLimit()}
                  disabled={isIncreasingLimit || !newAnnouncementLimit}
                >
                  <Plus aria-hidden />
                  {isIncreasingLimit
                    ? t.administration.users.increasingAnnouncementLimit
                    : t.administration.users.increaseAnnouncementLimit}
                </button>
              </div>
            )}

            <p
              id="admin-user-announcement-limit-help"
              className={`admin-user-announcement-limit-help${announcementLimitError ? ' error' : ''}`}
              role={announcementLimitError ? 'alert' : undefined}
            >
              {announcementLimitError || t.administration.users.announcementLimitHint}
            </p>
          </div>

          <div className="admin-user-detail-section">
            <label className="admin-user-detail-label">Informații de Contact:</label>
            <div className="admin-user-detail-contact-grid">
              {user.phoneNumber && (
                <div className="admin-user-detail-contact-item">
                  <div className="admin-user-detail-contact-icon">
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M2.66667 0C1.93333 0 1.33333 0.6 1.33333 1.33333V12.6667C1.33333 13.4 1.93333 14 2.66667 14H11.3333C12.0667 14 12.6667 13.4 12.6667 12.6667V1.33333C12.6667 0.6 12.0667 0 11.3333 0H2.66667ZM2.66667 12.6667V1.33333H11.3333V12.6667H2.66667ZM7 11.3333C7.36667 11.3333 7.66667 11.0333 7.66667 10.6667C7.66667 10.3 7.36667 10 7 10C6.63333 10 6.33333 10.3 6.33333 10.6667C6.33333 11.0333 6.63333 11.3333 7 11.3333Z" fill="#596255"/>
                    </svg>
                  </div>
                  <span className="admin-user-detail-contact-text">{user.phoneNumber}</span>
                </div>
              )}
              {user.facebook && (
                <div className="admin-user-detail-contact-item">
                  <div className="admin-user-detail-contact-icon">
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.6667 7C13.6667 3.31667 10.6833 0.333333 7 0.333333C3.31667 0.333333 0.333334 3.31667 0.333334 7C0.333334 10.3167 2.75 13.0583 5.91667 13.5833V8.91667H4.25V7H5.91667V5.58333C5.91667 3.95 6.95 3 8.45833 3C9.175 3 9.91667 3.125 9.91667 3.125V4.75H9.1C8.33333 4.75 8.08333 5.20833 8.08333 5.66667V7H9.83333L9.54167 8.91667H8.08333V13.5833C11.25 13.0583 13.6667 10.3167 13.6667 7Z" fill="#596255"/>
                    </svg>
                  </div>
                  <span className="admin-user-detail-contact-text">{user.facebook}</span>
                </div>
              )}
              {user.instagram && (
                <div className="admin-user-detail-contact-item">
                  <div className="admin-user-detail-contact-icon">
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 3.58333C5.10833 3.58333 3.58333 5.10833 3.58333 7C3.58333 8.89167 5.10833 10.4167 7 10.4167C8.89167 10.4167 10.4167 8.89167 10.4167 7C10.4167 5.10833 8.89167 3.58333 7 3.58333ZM7 9.16667C5.8 9.16667 4.83333 8.2 4.83333 7C4.83333 5.8 5.8 4.83333 7 4.83333C8.2 4.83333 9.16667 5.8 9.16667 7C9.16667 8.2 8.2 9.16667 7 9.16667ZM11.0833 3.45833C11.0833 3.89167 10.7333 4.24167 10.3 4.24167C9.86667 4.24167 9.51667 3.89167 9.51667 3.45833C9.51667 3.025 9.86667 2.675 10.3 2.675C10.7333 2.675 11.0833 3.025 11.0833 3.45833ZM13.2917 4.24167C13.2417 3.24167 13.0083 2.35833 12.2917 1.64167C11.575 0.925 10.6917 0.691667 9.69167 0.641667C8.65833 0.583333 5.34167 0.583333 4.30833 0.641667C3.30833 0.691667 2.425 0.925 1.70833 1.64167C0.991667 2.35833 0.758334 3.24167 0.708334 4.24167C0.65 5.275 0.65 8.59167 0.708334 9.625C0.758334 10.625 0.991667 11.5083 1.70833 12.225C2.425 12.9417 3.30833 13.175 4.30833 13.225C5.34167 13.2833 8.65833 13.2833 9.69167 13.225C10.6917 13.175 11.575 12.9417 12.2917 12.225C13.0083 11.5083 13.2417 10.625 13.2917 9.625C13.35 8.59167 13.35 5.275 13.2917 4.24167ZM11.9 10.7167C11.675 11.3083 11.2083 11.775 10.6167 12C9.79167 12.225 7.79167 12.1417 7 12.1417C6.20833 12.1417 4.20833 12.225 3.38333 12C2.79167 11.775 2.325 11.3083 2.1 10.7167C1.875 9.89167 1.95833 7.89167 1.95833 7C1.95833 6.10833 1.875 4.10833 2.1 3.28333C2.325 2.69167 2.79167 2.225 3.38333 2C4.20833 1.775 6.20833 1.85833 7 1.85833C7.79167 1.85833 9.79167 1.775 10.6167 2C11.2083 2.225 11.675 2.69167 11.9 3.28333C12.125 4.10833 12.0417 6.10833 12.0417 7C12.0417 7.89167 12.125 9.89167 11.9 10.7167Z" fill="#596255"/>
                    </svg>
                  </div>
                  <span className="admin-user-detail-contact-text">{user.instagram}</span>
                </div>
              )}
              {user.tiktok && (
                <div className="admin-user-detail-contact-item">
                  <div className="admin-user-detail-contact-icon">
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.91667 0H7.58333V9.52083C7.58333 10.5833 6.70833 11.4583 5.64583 11.4583C4.58333 11.4583 3.70833 10.5833 3.70833 9.52083C3.70833 8.45833 4.58333 7.58333 5.64583 7.58333V5.25C3.29167 5.25 1.375 7.16667 1.375 9.52083C1.375 11.875 3.29167 13.7917 5.64583 13.7917C8 13.7917 9.91667 11.875 9.91667 9.52083V4.66667C10.7917 5.25 11.8542 5.54167 12.9167 5.54167V3.20833C11.375 3.20833 10.2083 2.04167 9.91667 0.583333V0Z" fill="#596255"/>
                    </svg>
                  </div>
                  <span className="admin-user-detail-contact-text">{user.tiktok}</span>
                </div>
              )}
            </div>
          </div>

          {complaints && complaints.length > 0 && (
            <div className="admin-user-detail-section">
              <label className="admin-user-detail-label">Reclamații ({complaints.length}):</label>
              <div className="admin-user-detail-complaints-list">
                {complaints.map((complaint, index) => (
                  <div key={index} className="admin-user-detail-complaint-item">
                    <div className="admin-user-detail-complaint-icon">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6.99967 0.333008C3.31967 0.333008 0.333008 3.31967 0.333008 6.99967C0.333008 10.6797 3.31967 13.6663 6.99967 13.6663C10.6797 13.6663 13.6663 10.6797 13.6663 6.99967C13.6663 3.31967 10.6797 0.333008 6.99967 0.333008ZM7.66634 10.333H6.33301V8.99967H7.66634V10.333ZM7.66634 7.66634H6.33301V3.66634H7.66634V7.66634Z" fill="#B12A00"/>
                      </svg>
                    </div>
                    <span className="admin-user-detail-complaint-text">{complaint}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {onBlockUnblock && !isBlocked && (
            <div className="admin-user-detail-section admin-user-detail-block-reason">
              <label className="admin-user-detail-label" htmlFor="admin-user-block-reason">
                Motivul blocării (este trimis utilizatorului prin e-mail):
              </label>
              <textarea
                id="admin-user-block-reason"
                rows={3}
                maxLength={1000}
                value={blockReason}
                onChange={(event) => setBlockReason(event.target.value)}
              />
            </div>
          )}
        </div>

        <div className="admin-user-detail-modal-actions">
          <button className="admin-user-detail-btn-close" onClick={onClose}>
            Închide
          </button>
          {onViewDetails && (
            <button className="admin-user-detail-btn-close" onClick={() => onViewDetails(user.id)}>
              Vezi profilul
            </button>
          )}
          {onBlockUnblock && (
            <button 
              className={`admin-user-detail-btn-block ${isBlocked ? 'unblock' : ''}`}
              onClick={handleBlockUnblock}
              disabled={!isBlocked && !blockReason.trim()}
            >
              {isBlocked ? 'Deblochează Utilizator' : 'Blochează Utilizator'}
            </button>
          )}
        </div>
      </div>

      {showImagePreview && user.imageUrl && (
        <div className="admin-user-image-preview-overlay" onClick={() => setShowImagePreview(false)}>
          <div className="admin-user-image-preview-container">
            <button className="admin-user-image-preview-close" onClick={() => setShowImagePreview(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img
              src={user.imageUrl}
              alt={`${user.firstName} ${user.lastName}`}
              className="admin-user-image-preview"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetailModal;
